/** Pathfinder intro page content (UI; subscriptions API later). */

export const PATHFINDER_ROUTE_NAME = "PathfinderIntro";
export const PATHFINDER_PATH = "/ipromiseafrica/pathfinder";

export const PATHFINDER_SPORTS = Object.freeze([
  { slug: "football", name: "Football", icon: "fa-solid fa-futbol" },
  { slug: "basketball", name: "Basketball", icon: "fa-solid fa-basketball" },
  { slug: "rugby", name: "Rugby", icon: "fa-solid fa-football" },
  { slug: "volleyball", name: "Volleyball", icon: "fa-solid fa-volleyball" },
  { slug: "tennis", name: "Tennis", icon: "fa-solid fa-table-tennis-paddle-ball" },
]);

/** Condensed ecosystem pillars shown on the intro page. */
export const PATHFINDER_PILLARS = Object.freeze([
  {
    id: "parents",
    title: "Parental support",
    icon: "fa-solid fa-house-chimney-user",
    summary:
      "Talent starts at home. Parents spot patterns, ask the right questions, and stay involved—from first tournament to weekly practice.",
  },
  {
    id: "mentorship",
    title: "Mentorship",
    icon: "fa-solid fa-people-arrows",
    summary:
      "Monthly sessions with experienced athletes and coaches—discipline, resilience, and real career lessons beyond the scoreboard.",
  },
  {
    id: "coaching",
    title: "Training & coaching",
    icon: "fa-solid fa-person-running",
    summary:
      "Technical skill, game intelligence, emotional balance, body mechanics, and diet—developed through guided coaching, not guesswork.",
  },
  {
    id: "equipment",
    title: "Equipment access",
    icon: "fa-solid fa-basketball",
    summary:
      "Gear at different price points inside the app so every child can train with what fits their needs and budget.",
  },
  {
    id: "consistency",
    title: "Consistency",
    icon: "fa-solid fa-calendar-check",
    summary:
      "Daily, structured routines build discipline and mastery—the habits that separate flash-in-the-pan moments from lasting growth.",
  },
  {
    id: "tracking",
    title: "Performance tracking",
    icon: "fa-solid fa-chart-line",
    summary:
      "Goals, assists, rebounds, points, and progression across teams—development measured over time, not one tournament alone.",
  },
  {
    id: "agency",
    title: "Agency support",
    icon: "fa-solid fa-handshake",
    summary:
      "Connections to sponsorships, scouting networks, trials, and higher-level competition when the athlete is ready.",
  },
  {
    id: "timeline",
    title: "Time framework",
    icon: "fa-solid fa-road",
    summary:
      "Clear age-based stages from first touch to elite readiness—focused goals at every step of the journey.",
  },
]);

/**
 * Five programme modules (numbered in product spec).
 * `label` is the single friendly word for plan cards.
 */
export const PATHFINDER_PROGRAMME = Object.freeze([
  {
    id: "spotting",
    number: 1,
    label: "Spotting",
    title: "Talent spotting framework",
    icon: "fa-solid fa-magnifying-glass",
    summary:
      "Find adaptable, coordinated, motivated kids—who learns fastest, moves naturally, and keeps trying without pressure.",
    detail: "Avoid confusing early performance with real long-term talent.",
    basicPlan: false,
    fullPlan: true,
  },
  {
    id: "mentorship",
    number: 2,
    label: "Mentorship",
    title: "Digital mentorship platform",
    icon: "fa-solid fa-user-graduate",
    summary:
      "Mentors assign athletes, track progress, upload evaluations, and recommend next steps—talent visible across locations.",
    detail: "Partner programmes with organisations; mentor appreciation built in.",
    basicPlan: false,
    fullPlan: true,
  },
  {
    id: "coaching",
    number: 3,
    label: "Coaching",
    title: "Visual coaching framework",
    icon: "fa-solid fa-video",
    summary:
      "Videos and images for movement, coordination, physical base, game awareness, and mental traits—confidence and resilience included.",
    detail: "Movement, timing, speed, decisions, and sportsmanship in one structured path.",
    basicPlan: true,
    fullPlan: true,
  },
  {
    id: "tracking",
    number: 4,
    label: "Tracking",
    title: "Development tracking",
    icon: "fa-solid fa-chart-simple",
    summary:
      "Measure growth—not just competition. A trajectory that tells a richer story than a single tournament result.",
    detail: "Tiered ages 4–7, 8–11, 12–14, and 15–16 with clear development milestones.",
    basicPlan: true,
    fullPlan: true,
  },
  {
    id: "pathways",
    number: 5,
    label: "Pathways",
    title: "Agency & opportunities",
    icon: "fa-solid fa-door-open",
    summary:
      "Connector between athletes and the next level—sponsorship status, trial invitations, and pathway management.",
    detail: "When performance and readiness align, doors open faster.",
    basicPlan: false,
    fullPlan: true,
  },
]);

export const PATHFINDER_AGE_TIERS = Object.freeze([
  {
    tier: 1,
    ages: "4–7",
    title: "Foundation & potential",
    question: "Can the child move, learn, and enjoy sport?",
  },
  {
    tier: 2,
    ages: "8–11",
    title: "Skill acquisition",
    question: "How quickly can sport-specific skills develop?",
  },
  {
    tier: 3,
    ages: "12–14",
    title: "Development & specialization",
    question: "What is the athlete's long-term potential?",
  },
  {
    tier: 4,
    ages: "15–16",
    title: "Performance readiness",
    question: "Ready for elite pathways and high-level competition?",
  },
]);

export const PATHFINDER_PLANS = Object.freeze([
  {
    id: "basic",
    name: "Basic",
    price: 500,
    period: "month",
    perChild: true,
    highlight: false,
    tagline: "Coaching visuals and development tracking for one child.",
    includes: ["coaching", "tracking"],
    cta: "Start Basic plan",
  },
  {
    id: "full",
    name: "Full",
    price: 5000,
    period: "month",
    perChild: true,
    highlight: true,
    tagline: "The complete Pathfinder ecosystem—every pillar, every pathway.",
    includes: ["spotting", "mentorship", "coaching", "tracking", "pathways"],
    cta: "Start Full plan",
  },
]);
